import React, { useMemo } from 'react';
import { ThemeProvider, createTheme, CssBaseline } from '@mui/material';
import { useAppSelector } from '../store/hooks';

/**
 * MUI Theme Provider that integrates with Redux themeSlice
 * 
 * This component creates a MUI theme based on the Redux theme state,
 * ensuring all MUI components respect the app-wide dark/light mode.
 */
export const MuiThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const themeMode = useAppSelector((state) => state.theme.mode);
  const themeColors = useAppSelector((state) => state.theme.colors);

  const muiTheme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: themeMode,
          ...(themeMode === 'dark'
            ? {
                // Dark mode palette
                primary: {
                  main: '#38BDF8', // Cyan/blue accent from themeSlice
                  light: '#7DD3FC',
                  dark: '#0284C7',
                  contrastText: '#FFFFFF',
                },
                secondary: {
                  main: '#1E293B', // Dark blue-gray from themeSlice
                  light: '#334155',
                  dark: '#0F172A',
                  contrastText: '#F8FAFC',
                },
                background: {
                  default: '#0F172A', // Dark blue-gray from themeSlice
                  paper: '#1E293B', // Slightly lighter for cards
                },
                text: {
                  primary: '#F8FAFC', // Near white from themeSlice
                  secondary: '#94A3B8', // Match Tailwind gray-400 for consistency
                },
                divider: 'rgba(148, 163, 184, 0.12)', // Match gray-400 for consistency
                success: {
                  main: '#10B981', // Green
                  light: '#34D399',
                  dark: '#059669',
                },
                error: {
                  main: '#EF4444', // Red
                  light: '#F87171',
                  dark: '#DC2626',
                },
                warning: {
                  main: '#F59E0B', // Amber
                  light: '#FBBF24',
                  dark: '#D97706',
                },
                info: {
                  main: '#3B82F6', // Blue
                  light: '#60A5FA',
                  dark: '#2563EB',
                },
              }
            : {
                // Light mode palette
                primary: {
                  main: '#0EA5E9', // Sky blue from themeSlice
                  light: '#38BDF8',
                  dark: '#0284C7',
                  contrastText: '#FFFFFF',
                },
                secondary: {
                  main: '#E2E8F0', // Light gray from themeSlice
                  light: '#F1F5F9',
                  dark: '#CBD5E1',
                  contrastText: '#0F172A',
                },
                background: {
                  default: '#FFFFFF', // White from themeSlice
                  paper: '#F8FAFC', // Very light gray for cards
                },
                text: {
                  primary: '#0F172A', // Dark blue-gray from themeSlice
                  secondary: '#64748B', // Medium gray for secondary text
                },
                divider: 'rgba(15, 23, 42, 0.12)', // Subtle dividers
              }),
        },
        typography: {
          fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
          h1: {
            fontWeight: 600,
          },
          h2: {
            fontWeight: 600,
          },
          h3: {
            fontWeight: 600,
          },
          h4: {
            fontWeight: 600,
          },
          h5: {
            fontWeight: 600,
          },
          h6: {
            fontWeight: 600,
          },
          subtitle1: {
            fontWeight: 500,
          },
          subtitle2: {
            fontWeight: 500,
          },
        },
        shape: {
          borderRadius: 8, // Consistent with rounded-lg in Tailwind
        },
        components: {
          MuiCssBaseline: {
            styleOverrides: {
              body: {
                scrollbarColor: themeMode === 'dark' ? '#475569 #1E293B' : '#CBD5E1 #F1F5F9',
                '&::-webkit-scrollbar, & *::-webkit-scrollbar': {
                  width: 8,
                  height: 8,
                },
                '&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb': {
                  borderRadius: 8,
                  backgroundColor: themeMode === 'dark' ? '#475569' : '#CBD5E1',
                  minHeight: 24,
                },
                '&::-webkit-scrollbar-thumb:hover, & *::-webkit-scrollbar-thumb:hover': {
                  backgroundColor: themeMode === 'dark' ? '#64748B' : '#94A3B8',
                },
                '&::-webkit-scrollbar-track, & *::-webkit-scrollbar-track': {
                  backgroundColor: themeMode === 'dark' ? '#1E293B' : '#F1F5F9',
                },
              },
            },
          },
          MuiTooltip: {
            styleOverrides: {
              popper: {
                zIndex: 9999, // Ensure tooltips appear above all other elements
              },
            },
          },
          MuiCard: {
            styleOverrides: {
              root: {
                backgroundImage: 'none', // Remove default gradient
              },
            },
          },
          MuiPaper: {
            styleOverrides: {
              root: {
                backgroundImage: 'none', // Remove default gradient
              },
            },
          },
        },
      }),
    [themeMode]
  );

  return (
    <ThemeProvider theme={muiTheme}>
      <CssBaseline />
      {children}
    </ThemeProvider>
  );
};
